/**
 * The collect mode: the state behind "draw, draw, done".
 *
 * Marking an asset takes a region per relevant area of the drawing — the symbol,
 * the tag balloon, any caption worth reading — so a draw mode that ends at the first
 * mouse-up cannot express it. The mode stays armed instead, and each drag appends a
 * region to a draft the workspace owns. The canvas reports drags and renders the
 * draft it is handed; it holds none of this itself.
 *
 * Nothing persists until done: one create call and one identify call fire together
 * at the end, so an abandoned drawing leaves no row behind.
 */
import type { BoundingBox, IdentifyAssetKind } from '../types';

/** Which element kind a collected draft belongs to. */
export type GeometryTargetKind =
  | 'equipment'
  | 'equipmentInstrument'
  | 'node'
  | 'nodeInstrument'
  | 'controlLoop';

/**
 * An existing element the next drawing belongs to.
 *
 * `replace` is the redraw: the new regions become the element's whole list. `append`
 * adds to what is already there, which is what "Add region" in the instrument editor
 * arms. Absent entirely when the SME is drawing something new.
 */
export interface GeometryTarget {
  kind: GeometryTargetKind;
  id: string;
  op: 'replace' | 'append';
}

/** The regions collected so far, and what they are being collected for. */
export interface BoxDraft {
  kind: IdentifyAssetKind;
  /** The element being redrawn or added to. Absent while drawing a new element. */
  target?: GeometryTarget;
  boxes: BoundingBox[];
}

/** The hint the canvas shows while a draft is armed but the SME has drawn nothing yet. */
export const COLLECT_ARMING_HINT = 'Draw the symbol first, then the balloon and captions';

/**
 * Arms the mode. A new marking starts empty; redrawing an existing one starts from
 * the regions it already has, so Escape pops those too and the SME sees one list
 * rather than a saved set and a separate draft.
 */
export function armDraft(
  kind: IdentifyAssetKind,
  target?: GeometryTarget,
  seedRegions: BoundingBox[] = [],
): BoxDraft {
  return { kind, target, boxes: seedRegions };
}

export function appendRegion(draft: BoxDraft, box: BoundingBox): BoxDraft {
  return { ...draft, boxes: [...draft.boxes, box] };
}

/**
 * Backs out the last region. An empty draft has nothing to back out of, so Escape
 * there means "leave the collect mode" and this answers `null`.
 */
export function removeLastRegion(draft: BoxDraft): BoxDraft | null {
  if (draft.boxes.length === 0) return null;
  return { ...draft, boxes: draft.boxes.slice(0, -1) };
}

/**
 * Replaces one region of an element in place, keeping the rest and the singular
 * `bbox` in step. Only the first region reaches `bbox`, so moving a later region
 * leaves the element's anchor exactly where it was.
 */
export function withRegionAt<T extends { bbox: BoundingBox | null; bboxes?: BoundingBox[] }>(
  element: T,
  regionIndex: number,
  box: BoundingBox,
): T {
  const regions = element.bboxes && element.bboxes.length > 0
    ? element.bboxes
    : element.bbox ? [element.bbox] : [];
  const next = regions.length > 0
    ? regions.map((region, index) => (index === regionIndex ? box : region))
    : [box];
  return { ...element, bboxes: next, bbox: next[0] };
}

/** A draft is finishable once it holds a region: `bboxes` may never be empty. */
export function canFinish(draft: BoxDraft): boolean {
  return draft.boxes.length > 0;
}

/**
 * What the element's region list becomes when the draft is committed: the draft
 * alone for a new element or a redraw, the existing list plus the draft for an
 * append.
 */
export function committedRegions(draft: BoxDraft, existingRegions: BoundingBox[]): BoundingBox[] {
  if (draft.target?.op === 'append') return [...existingRegions, ...draft.boxes];
  return draft.boxes;
}
